<template>
  <page :name="$t('Dashboard')" :state="[productsState, paymentsState, transferState]" hide-breadcrumbs ignore-failed>
    <pf-page-section no-body-wrapper>
      <pf-title h="2">{{ $t('Di cosa hai bisogno?') }}</pf-title>

      <pf-grid gutter>
        <pf-grid-item v-if="login.is_reseller && !login.complete_skill" md="6" xl2="3">
          <pf-card data-ouia-component-id="dashboard-shortcut-skills">
            <pf-card-header>{{ $t('Aiutaci a passarti più clienti') }}</pf-card-header>
            <pf-card-body>
              <pf-button :to="{ name: 'my_skills' }" variant="link">
                <template #icon><puzzle-piece-icon /></template>
                {{ $t('Compila le tue skill') }}
              </pf-button>
            </pf-card-body>
          </pf-card>
        </pf-grid-item>

        <pf-grid-item md="6" xl2="3">
          <pf-card data-ouia-component-id="dashboard-shortcut-wizard">
            <pf-card-header>{{ $t("Acquistare un servizio / creare un'offerta") }}</pf-card-header>
            <pf-card-body>
              <pf-button :to="{ name: 'diy-offer' }" variant="link" inline>
                <template #icon><wand-magic-icon /></template>
                {{ $t('Vai al wizard') }}
              </pf-button>
            </pf-card-body>
          </pf-card>
        </pf-grid-item>

        <pf-grid-item md="6" xl2="3">
          <pf-card data-ouia-component-id="dashboard-shortcut-ticket">
            <pf-card-header>{{ $t('Assistenza / Consulenza tecnica') }}</pf-card-header>
            <pf-card-body>
              <pf-button :to="{ name: 'tickets' }" variant="link" inline>
                <template #icon><ticket-icon /></template>
                {{ $t('Apri un ticket') }}
              </pf-button>
            </pf-card-body>
          </pf-card>
        </pf-grid-item>

        <pf-grid-item sm="12" md="6" xl2="3" v-if="askPartner">
          <pf-card data-ouia-component-id="dashboard-shortcut-partner">
            <pf-card-header>{{ $t('Diventare partner Artera') }}</pf-card-header>
            <pf-card-body>
              <pf-button :to="{ name: 'become_partner' }" variant="link" inline>
                <template #icon><handshake-icon /></template>
                {{ $t('Compila i tuoi dati e diventa partner') }}
              </pf-button>
            </pf-card-body>
          </pf-card>
        </pf-grid-item>
      </pf-grid>

      <ticket-list :title="$t('Ticket aperti')" only-open />

      <pf-title h="2" v-if="transfers.length > 0 || pending.length > 0 || testing.length > 0">
        {{ $t('Nuove attivazioni') }}
      </pf-title>

      <pf-grid gutter v-if="transfers.length > 0">
        <pf-grid-item md="6">
          <pf-card>
            <pf-card-header>
              <pf-card-title>
                <pf-title h="3">{{ $t('Trasferimenti in corso') }}</pf-title>
              </pf-card-title>
            </pf-card-header>
            <pf-card-body>
              <pf-data-list compact>
                <pf-data-list-item v-for="tbd in transfers" :key="tbd.dominio">
                  <pf-data-list-cell>{{ tbd.dominio }}</pf-data-list-cell>
                  <pf-data-list-action>
                    <pf-label v-for="t in tbd.transfers" :key="t.id">
                      {{ transferTitle(t) }}
                    </pf-label>
                    <pf-button to="/transfers" variant="link" small icon-position="end">
                      <template #icon><arrow-right-icon /></template>
                    </pf-button>
                  </pf-data-list-action>
                </pf-data-list-item>
              </pf-data-list>
            </pf-card-body>
            <pf-card-footer class="pf-v6-u-text-align-end">
              <pf-button to="/transfers" variant="link" icon-position="end">
                <template #icon><arrow-right-icon /></template>
                {{ $t('Vai alla pagina trasferimenti') }}
              </pf-button>
            </pf-card-footer>
          </pf-card>
        </pf-grid-item>
      </pf-grid>

      <pf-card v-if="pending.length > 0">
        <pf-card-header>
          <pf-card-title>
            <pf-title h="3">{{ $t('Prodotti in fase di attivazione') }}</pf-title>
            <p>
              {{ $t('I nostri tecnici provvederanno ad attivare i seguenti prodotti a breve.') }}
            </p>
          </pf-card-title>
        </pf-card-header>

        <pf-card-body>
          <pf-data-list>
            <pf-data-list-item v-for="product in pending" :key="product.id">
              <pf-data-list-cell style="max-width: 145px">
                <pf-title h="6">{{ $t('Ordine') }}:</pf-title> #{{ product.id_ordine }}
              </pf-data-list-cell>

              <pf-data-list-cell>
                <pf-title h="6">{{ $t('Prodotto') }}</pf-title>
                {{ product.nome }}
              </pf-data-list-cell>

              <pf-data-list-cell v-if="product.dominio">
                <pf-title h="6">{{ $t('Dominio') }}</pf-title>
                {{ product.dominio }}
              </pf-data-list-cell>
            </pf-data-list-item>
          </pf-data-list>
        </pf-card-body>
      </pf-card>

      <pf-card v-if="testing.length > 0">
        <pf-card-header>
          <pf-title h="3">{{ $t('Prodotti in prova') }}</pf-title>
        </pf-card-header>
        <pf-card-body>
          <product-list :products="testing" @submit="productsState.update" />
        </pf-card-body>
      </pf-card>

      <pf-title h="2">{{ $t('Nei prossimi giorni') }}</pf-title>

      <pf-grid gutter>
        <pf-grid-item xl="6">
          <pf-card>
            <pf-card-header>
              <pf-title h="3">{{ $t('Prodotti scaduti o in scadenza nei prossimi 30 giorni') }}</pf-title>
            </pf-card-header>
            <pf-card-body>
              <pf-data-list v-if="expiring.length" compact>
                <pf-data-list-item v-for="product in expiring" :key="product.id">
                  <pf-data-list-cell>{{ product.nome }} {{ product.dominio }}</pf-data-list-cell>
                  <pf-data-list-cell class="pf-v6-u-text-align-end" style="max-width: 90px">
                    {{ $datefromnow(product.data_fine) }}
                  </pf-data-list-cell>
                </pf-data-list-item>
              </pf-data-list>
              <pf-empty-state v-else variant="xs">
                <pf-empty-state-header :title="$t('Nessun prodotto in scadenza')" heading-level="h4">
                  <template #icon>
                    <pf-empty-state-icon>
                      <check-icon />
                    </pf-empty-state-icon>
                  </template>
                </pf-empty-state-header>
              </pf-empty-state>
            </pf-card-body>

            <pf-card-footer class="pf-v6-u-text-align-end">
              <pf-button to="/products" variant="link" icon-position="end">
                <template #icon><arrow-right-icon /></template>
                {{ $t('Visualizza tutti i prodotti') }}
              </pf-button>
            </pf-card-footer>
          </pf-card>
        </pf-grid-item>

        <pf-grid-item xl="6">
          <pf-card v-if="login.grants.billing">
            <pf-card-header>
              <pf-title h="3">{{ $t('Pagamenti scaduti o in scadenza') }}</pf-title>
            </pf-card-header>
            <pf-card-body>
              <pf-data-list v-if="payments.length" compact>
                <pf-data-list-item v-for="payment in payments" :key="payment.id">
                  <pf-data-list-cell>
                    <template v-if="payment.fattura">
                      {{ $t('Fattura nr. {0}:', [payment.fattura]) }}
                    </template>
                    {{ payment.descrizione }}
                  </pf-data-list-cell>

                  <pf-data-list-cell style="max-width: 160px">
                    {{ $currency(payment.amount, login.currency) }}
                  </pf-data-list-cell>

                  <pf-data-list-cell>
                    {{ $datefromnow(payment.data) }}
                    <span v-if="parseDateTime(payment.data).diff(now, ['days']).days < -30" class="pf-v6-u-text-color-status-danger">
                      {{ $t('Insoluto - possibile sospensione servizi') }}
                    </span>
                    <span v-else-if="parseDateTime(payment.data).diff(now, ['days']).days < 0" class="pf-v6-u-text-color-status-danger">
                      {{ $t('Insoluto') }}
                    </span>
                  </pf-data-list-cell>

                  <pf-data-list-action>
                    <pf-button :to="{ name: 'payment', params: { id: payment.id } }" variant="link" icon-position="end">
                      <template #icon><circle-arrow-right-icon /></template>
                    </pf-button>
                  </pf-data-list-action>
                </pf-data-list-item>
              </pf-data-list>

              <pf-empty-state v-else variant="xs">
                <pf-empty-state-header :title="$t('Nessun pagamento in scadenza')" heading-level="h4">
                  <template #icon>
                    <pf-empty-state-icon>
                      <check-icon />
                    </pf-empty-state-icon>
                  </template>
                </pf-empty-state-header>
              </pf-empty-state>
            </pf-card-body>

            <pf-card-footer class="pf-v6-u-text-align-end">
              <pf-button to="/invoices" variant="link" icon-position="end">
                <template #icon><arrow-right-icon /></template>
                {{ $t('Visualizza tutte le scadenze') }}
              </pf-button>
            </pf-card-footer>
          </pf-card>
        </pf-grid-item>
      </pf-grid>
    </pf-page-section>
  </page>

  <prodotti-popup-renewal-change
    v-if="changingProduct.product !== null && changingProduct.variant !== null && changingProduct.renew !== null"
    :idProduct="changingProduct.product.id"
    :variant="changingProduct.variant"
    :renew="changingProduct.renew"
    @submit="renewalChangeSubmit"
    @close="setChangingProduct(null, null, null)"
  />
</template>

<script lang="ts" setup>
import { ref, type Ref, computed } from 'vue';

import { asyncComputedList } from '@common/asyncComputed';
import { by } from '@common/utils';

import TicketList from '../components/ticket-list.vue';
import ProdottiPopupRenewalChange from '../components/prodotti-popup-renewal-change.vue';
import ProductList from '@/components/product-list.vue';
import ArrowRightIcon from '@vue-patternfly/icons/arrow-right-icon';
import PuzzlePieceIcon from '@vue-patternfly/icons/puzzle-piece-icon';
import TicketIcon from '@vue-patternfly/icons/ticket-icon';
import WandMagicIcon from '@vue-patternfly/icons/wand-magic-icon';
import HandshakeIcon from '@vue-patternfly/icons/handshake-icon';
import CircleArrowRightIcon from '@vue-patternfly/icons/circle-arrow-right-icon';
import CheckIcon from '@vue-patternfly/icons/check-icon';

import { useLoginStore } from '@/store/login';
import { ProductOrdersResource, type ProductOrder, PaymentsResource, type Payment, TransferResource } from '@/resources';
import { DateTime } from 'luxon';
import { parseDateTime } from '@common/date';
import { transferTitle } from '@/components/transfers-list.vue';

const login = useLoginStore();

const { state: transferState, result: transfers } = asyncComputedList(() => new TransferResource({ just_ongoing: '1' }).get());
const { state: productsState, result: products } = asyncComputedList(() => new ProductOrdersResource({ sort: 'data_fine' }).get());
const { state: paymentsState, result: payments } = asyncComputedList(() => {
  if (!login.grants.billing) {
    return [] as Payment[];
  }
  return new PaymentsResource({ exiring: 30 }).get();
});

const now = DateTime.now();

const changingProduct: Ref<{
  product: ProductOrder | null;
  variant: 'state' | 'autorenew' | null;
  renew: boolean | null;
}> = ref({
  product: null,
  variant: 'state',
  renew: null,
});

const testing = computed(() => products.value.filter(p => p.free_period > 0).sort(by('data_fine')));
const pending = computed(() => products.value.filter(p => p.stato_tecnico === 'attivare').sort(by('data_fine')));

const expiring = computed(() => {
  return products.value.filter(p => parseDateTime(p.data_fine).diff(now, ['days']).days < 31).sort(by('data_fine'));
});

const askPartner = computed(() => {
  return !login.is_reseller && !login.has_reseller;
});

function setChangingProduct(product: ProductOrder | null, variant: 'state' | 'autorenew' | null, renew: boolean | null) {
  changingProduct.value.product = product;
  changingProduct.value.variant = variant;
  changingProduct.value.renew = renew;
}

function renewalChangeSubmit() {
  setChangingProduct(null, null, null);
  productsState.update();
}
</script>
